import { Button as AntButton, ButtonProps as AntButtonProps } from 'antd';
import { forwardRef } from 'react';

import { handleKeyboardClick } from '../../utils';
import { getClassName } from '../_utils';

import './Button.scss';

export interface ButtonProps extends Pick<
  AntButtonProps,
  | 'aria-label'
  | 'children'
  | 'className'
  | 'disabled'
  | 'href'
  | 'icon'
  | 'iconPosition'
  | 'id'
  | 'onClick'
  | 'onKeyDown'
  | 'role'
  | 'shape'
  | 'size'
  | 'style'
  | 'tabIndex'
  | 'target'
  | 'title'
  | 'type'
> {
  intent?: ButtonIntent;
  'data-testid'?: string;
}

export type ButtonIntent =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'tertiary' // This is a misnomer, preserved for backwards compatibility. It is always used to represent indicate a functionally primary button
  | 'submit'
  | 'danger'
  | 'link'
  | 'text'
  | 'icon';

const fontWeightMap: Partial<Record<ButtonIntent, number | 'inherit'>> = {
  submit: 700,
  danger: 700,
  link: 'inherit',
  text: 'inherit',
  icon: 'inherit',
};

const typeMap: Record<ButtonIntent, AntButtonProps['type'] | undefined> = {
  default: 'default',
  primary: 'primary',
  secondary: 'default',
  tertiary: 'primary',
  submit: 'primary',
  danger: undefined,
  link: 'link',
  text: 'text',
  icon: 'text',
};

export const Button = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  ButtonProps
>(function Button({ className, intent, children, style, ...props }, ref) {
  const type = typeMap[intent ?? 'default'];
  return (
    <AntButton
      ref={ref}
      type={type}
      htmlType={intent === 'submit' ? 'submit' : undefined}
      className={getClassName('button', className)}
      tabIndex={props.tabIndex ?? 0}
      aria-label={
        props['aria-label'] ||
        (typeof children === 'string' ? children : undefined)
      }
      onKeyDown={(e) => {
        handleKeyboardClick(e);
        if (typeof props.onKeyDown === 'function') props.onKeyDown(e);
      }}
      {...{
        shape: intent === 'icon' ? 'circle' : 'round',
        variant: intent === 'danger' ? 'solid' : undefined,
        color: intent === 'danger' ? 'danger' : undefined,
        rel: props.target === '_blank' ? 'noopener noreferrer' : undefined,
        ...props,
      }}
      style={{
        fontWeight: fontWeightMap[intent ?? 'default'],
        ...style,
      }}
      data-intent={intent}
    >
      {children}
    </AntButton>
  );
});
